import { ArchBaseService } from "@/common/api.service";

/**
 * Service for backend verification of Entra ID authentication
 * Handles the secure ID token validation flow with the backend
 */
class EntraIdBackendAuthService {
  /**
   * Normalize email address for backend verification
   * @param {string} email - Raw email address
   * @returns {string} Normalized email (lowercase, trimmed)
   */
  normalizeEmail(email) {
    if (!email) return "";
    return email.toLowerCase().trim();
  }

  /**
   * Extract identity from MSAL result with fallbacks
   * Tries multiple claims in order: mail, preferred_username, upn, oid
   *
   * @param {Object} msalResult - MSAL authentication result
   * @returns {Object} Extracted identity information
   */
  extractIdentity(msalResult) {
    const profile = msalResult.profile || {};
    const account = msalResult.account || {};
    const idTokenClaims = account.idTokenClaims || {};

    // Try to get email from multiple sources
    const email =
      profile.mail ||
      account.userName ||
      idTokenClaims.preferred_username ||
      idTokenClaims.upn ||
      null;

    // Object ID as fallback identifier
    const oid = idTokenClaims.oid || account.accountIdentifier || null;

    return {
      email: this.normalizeEmail(email),
      oid,
      preferredUsername: idTokenClaims.preferred_username,
      upn: idTokenClaims.upn,
      tenantId: idTokenClaims.tid || process.env.VUE_APP_ENTRA_TENANT_ID,
    };
  }

  /**
   * Verify Entra ID authentication with backend
   * Sends the ID token to backend for validation
   *
   * @param {Object} params - Authentication parameters
   * @param {string} params.idToken - Microsoft Entra ID token (raw JWT)
   * @param {string} params.email - User email from Entra ID
   * @param {Object} params.msalResult - Full MSAL result (optional, for better identity extraction)
   * @param {boolean} params.isRefresh - Whether this is a token refresh (default: false)
   * @returns {Promise<Object>} Backend response with JWT token and user data
   */
  async verifyWithBackend({
    idToken,
    email,
    msalResult = null,
    isRefresh = false,
  }) {
    const startTime = Date.now();
    let identity = null;

    try {
      if (!idToken) {
        throw new Error("ID token is required");
      }

      // Extract and normalize identity
      if (msalResult) {
        identity = this.extractIdentity(msalResult);
        email = identity.email;
      } else {
        email = this.normalizeEmail(email);
      }

      if (!email) {
        throw new Error(
          "Email is required and could not be extracted from MSAL result",
        );
      }

      // Structured logging with telemetry
      const logContext = {
        operation: isRefresh ? "backend_token_refresh" : "backend_verification",
        email,
        tenantId: identity?.tenantId || "unknown",
        timestamp: new Date().toISOString(),
      };

      console.log(
        `🔐 ${isRefresh ? "Refreshing" : "Verifying"} Entra ID authentication with backend...`,
      );
      console.log("📊 Context:", logContext);

      // Initialize API service
      ArchBaseService.init();

      // NOTE: We intentionally DO NOT check if user exists before verification
      // to prevent user enumeration attacks (security best practice)
      // The backend will return a generic error for both "user not found" and "invalid token"

      console.log("🌐 Making POST request to backend:", {
        endpoint: "auth/entra-id/verify",
        email: email,
        hasIdToken: !!idToken,
        idTokenLength: idToken?.length,
        requestTimestamp: new Date().toISOString()
      });

      // Call backend verification endpoint
      const response = await ArchBaseService.post("auth/entra-id/verify", {
        idToken,
        email,
      });

      console.log("📥 Backend API response received:", {
        status: response.status,
        hasData: !!response.data,
        hasToken: !!response.data?.token,
        responseTimestamp: new Date().toISOString()
      });

      if (response.status === 200 && response.data) {
        const duration = Date.now() - startTime;

        // Structured success logging
        console.log("✅ Backend verification successful - JWT token received!");
        console.log("🎫 JWT Token Details:", {
          tokenReceived: !!response.data.token,
          tokenLength: response.data.token?.length,
          tokenPrefix: response.data.token?.substring(0, 20) + "...",
        });
        console.log("📊 Success telemetry:", {
          duration: `${duration}ms`,
          email,
          userId: response.data.userid,
          username: response.data.username,
          operation: isRefresh ? "refresh" : "verify",
        });

        // Backend response received successfully

        // Extract and store backend token expiry if available
        let tokenExpiry = null;
        if (response.data.token) {
          try {
            const tokenParts = response.data.token.split(".");
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              tokenExpiry = payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
            }
          } catch (e) {
            console.warn("⚠️ Could not decode backend token expiry");
          }
        }

        // Backend returns same structure as traditional login:
        // { code, message, token, username, firstname, lastname, branch, userid }
        const result = {
          success: true,
          token: response.data.token,
          tokenExpiry,
          user: {
            email: response.data.username,
            userid: response.data.userid,
            firstname: response.data.firstname,
            lastname: response.data.lastname,
            branch: response.data.branch,
          },
          code: response.data.code,
          message: response.data.message,
        };

        return result;
      }

      // Handle non-200 successful responses
      return {
        success: false,
        error: response.data?.code || "VERIFICATION_FAILED",
        message: response.data?.message || "Backend verification failed",
      };
    } catch (error) {
      const duration = Date.now() - startTime;

      // Structured error logging with telemetry
      console.error("❌ Backend verification failed");
      console.error("📊 Error telemetry:", {
        duration: `${duration}ms`,
        email,
        status: error.response?.status,
        errorCode: error.response?.data?.code,
        tenantId: identity?.tenantId,
        operation: isRefresh ? "refresh" : "verify",
      });
      console.error("Error details:", error);

      // Handle specific error cases
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        // 401 Unauthorized from backend
        // Note: Backend returns generic error for both "user not found" and "invalid token"
        // to prevent user enumeration attacks
        if (status === 401) {
          console.error("🔴 401 Unauthorized from backend");
          console.error("📋 Diagnostic info (not shown to user):", {
            email,
            oid: identity?.oid,
            preferredUsername: identity?.preferredUsername,
            upn: identity?.upn,
            possibleCauses: [
              "User not provisioned in backend",
              "ID token validation failed",
              "Email mismatch with backend records",
              "Required scopes/audience mismatch",
            ],
          });

          // Generic error message (don't reveal if user exists or not)
          return {
            success: false,
            error: data?.code || "AUTHENTICATION_FAILED",
            message:
              data?.message ||
              "Authentication failed. Please check your credentials or contact support.",
          };
        }

        // Other HTTP errors
        if (status === 403) {
          console.error("🔴 403 Forbidden - scope/role issue");
          return {
            success: false,
            error: data?.code || "FORBIDDEN",
            message: data?.message || "Access forbidden.",
          };
        }

        if (status === 404) {
          console.error("🔴 404 Not Found - endpoint does not exist");
          return {
            success: false,
            error: data?.code || "NOT_FOUND",
            message: data?.message || "Endpoint not found.",
          };
        }

        // Other backend errors
        return {
          success: false,
          error: data?.code || "BACKEND_ERROR",
          message:
            data?.message ||
            error.message ||
            "An error occurred during authentication.",
        };
      }

      // Network or other errors
      console.error("🔴 Network error - cannot reach backend");
      return {
        success: false,
        error: "NETWORK_ERROR",
        message:
          "Unable to connect to authentication server. Please try again.",
      };
    }
  }

  /**
   * Check if a user exists in the backend system
   *
   * ⚠️ SECURITY WARNING: This method is NOT used in the verification flow
   * to prevent user enumeration attacks. Backend recommendation is to return
   * generic errors rather than revealing whether a user exists.
   *
   * This method is kept for potential future internal diagnostics/debugging only.
   * DO NOT call this method before user verification.
   *
   * @param {string} email - User email to check
   * @returns {Promise<boolean>} Whether user exists
   */
  async checkUserExists(email) {
    try {
      ArchBaseService.init();

      const response = await ArchBaseService.get(
        `auth/user/exists?email=${encodeURIComponent(email)}`,
      );

      return response.data?.exists === true;
    } catch (error) {
      console.error("Failed to check user existence:", error);
      return false;
    }
  }
}

export default new EntraIdBackendAuthService();
